import { useState, useEffect, useCallback, useMemo } from 'react';
import { Pagination } from 'react-bootstrap';
import { Spinner } from 'reactstrap';
import { toast } from 'react-toastify';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';

import { getVoiceLeads, updateVoiceLeadStatus } from './FormikHelpers/Api';
import { LEAD_STATUS_COLORS, LEAD_STATUSES, formatDate, formatDuration } from './FormikHelpers/InitialValues';

const PER_PAGE = 15;

// ─── StatusBadge ──────────────────────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const st = LEAD_STATUS_COLORS[status] || LEAD_STATUS_COLORS.new;
  return (
    <span
      className={`va-leads__status-badge va-leads__status-badge--${status || 'new'}`}
      style={{
        display: 'inline-block',
        padding: '3px 10px',
        borderRadius: 20,
        fontSize: 11,
        fontWeight: 700,
        color: st.color,
        background: st.background,
        border: `1px solid ${st.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {status || 'new'}
    </span>
  );
};

// ─── TranscriptPreview ────────────────────────────────────────────────────────

const TranscriptPreview = ({ transcript }) => {
  const [open, setOpen] = useState(false);
  if (!transcript) return <span style={{ fontSize: 11, color: '#CBD5E1' }}>—</span>;

  const preview = transcript.length > 80 ? transcript.slice(0, 80) + '…' : transcript;

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          background: '#EFF6FF', border: '1px solid #BFDBFE',
          borderRadius: 6, padding: '2px 8px',
          fontSize: 11, fontWeight: 600, color: '#1D4ED8', cursor: 'pointer',
        }}
      >
        <FeatherIcon icon={open ? 'chevron-up' : 'chevron-down'} size={11} />
        View Transcript
      </button>
      {open && (
        <div
          style={{
            marginTop: 6, padding: '8px 10px', background: '#F8FAFC',
            border: '1px solid #E2E8F0', borderRadius: 7,
            fontSize: 11, color: '#334155', lineHeight: 1.6,
            maxHeight: 140, overflowY: 'auto', whiteSpace: 'pre-wrap',
          }}
        >
          {transcript}
        </div>
      )}
    </div>
  );
};

// ─── VoiceLeadsTable ──────────────────────────────────────────────────────────

const VoiceLeadsTable = ({ configId, configs = [] }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [updatingId, setUpdatingId] = useState(null);

  // ─── Data fetch ─────────────────────────────────────────────────────────

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (configId) params.config_id = configId;
      if (statusFilter) params.status = statusFilter;
      if (sourceFilter) params.source = sourceFilter;
      const res = await getVoiceLeads(params);
      setLeads(res?.data || []);
      setCurrentPage(1);
    } catch {
      toast.error('Failed to fetch voice leads');
    } finally {
      setLoading(false);
    }
  }, [configId, statusFilter, sourceFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  // ─── Status change ───────────────────────────────────────────────────────

  const handleStatusChange = useCallback(async (leadId, newStatus) => {
    setUpdatingId(leadId);
    try {
      await updateVoiceLeadStatus(leadId, newStatus);
      setLeads((prev) => prev.map((l) => l._id === leadId ? { ...l, status: newStatus } : l));
      toast.success('Lead status updated');
    } catch { /* handled in api layer */ }
    finally { setUpdatingId(null); }
  }, []);

  // ─── Pagination ──────────────────────────────────────────────────────────

  const paged = useMemo(() => leads.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE), [leads, currentPage]);
  const totalPages = Math.ceil(leads.length / PER_PAGE);

  // ─── Config name lookup ──────────────────────────────────────────────────

  const configName = (cfgId) => {
    const c = configs.find((x) => x._id === cfgId || x._id?.toString() === cfgId?.toString());
    return c?.persona?.assistant_name || c?.voice_slug || '—';
  };

  // ─── Select style (dark header) ──────────────────────────────────────────

  const selectStyle = {
    padding: '6px 10px', borderRadius: 8,
    border: '1px solid rgba(147,197,253,0.2)',
    background: 'rgba(255,255,255,0.06)', color: '#93C5FD',
    fontSize: 12, cursor: 'pointer', outline: 'none',
  };

  return (
    <Paper elevation={0} style={{ borderRadius: 10, border: '1px solid #E2E8F0', boxShadow: '0 4px 24px rgba(15,28,46,0.08)', overflow: 'hidden' }}>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <div className="ri-header" style={{ padding: '13px 20px' }}>
        <div className="ri-header__left">
          <div className="ri-header__icon-wrap">
            <FeatherIcon icon="phone-incoming" size={16} color="#93C5FD" />
          </div>
          <div className="ri-header__meta">
            <div className="ri-header__label">Voice Assistant</div>
            <div className="ri-header__title">Leads</div>
          </div>
          <span className="ri-header__badge ri-header__badge--records">
            {leads.length} total
          </span>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
            <option value="">All Statuses</option>
            {LEAD_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} style={selectStyle}>
            <option value="">All Sources</option>
            <option value="inbound_call">Inbound</option>
            <option value="outbound_call">Outbound</option>
          </select>
        </div>
      </div>

      {/* ── Table ───────────────────────────────────────────────────────── */}
      <Box sx={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              {['Name', 'Email', 'Phone', 'Unit Interest', 'Callback Time', 'Assistant', 'Duration', 'Received', 'CRM', 'Status', ''].map((h) => (
                <th
                  key={h}
                  style={{
                    padding: '10px 14px', textAlign: 'left',
                    background: '#F8FAFC', fontWeight: 700, fontSize: 10.5,
                    color: '#64748B', borderBottom: '1px solid #E2E8F0',
                    whiteSpace: 'nowrap', letterSpacing: '0.4px',
                  }}
                >{h}</th>
              ))}
            </tr>
          </thead>

          <tbody>
            {loading && (
              <tr>
                <td colSpan={11}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40, gap: 12, color: '#64748B' }}>
                    <Spinner style={{ color: '#1D4ED8' }} /><span>Loading leads…</span>
                  </div>
                </td>
              </tr>
            )}

            {!loading && paged.length === 0 && (
              <tr>
                <td colSpan={11}>
                  <div style={{ padding: 48, textAlign: 'center' }}>
                    <FeatherIcon icon="phone-missed" size={28} color="#CBD5E1" />
                    <Typography style={{ fontWeight: 700, color: '#334155', fontSize: 14, marginTop: 10 }}>No Leads Yet</Typography>
                    <Typography style={{ color: '#94A3B8', fontSize: 12, marginTop: 4 }}>
                      Leads captured through voice calls will appear here.
                    </Typography>
                  </div>
                </td>
              </tr>
            )}

            {!loading && paged.map((lead, idx) => (
              <tr key={lead._id} style={{ background: idx % 2 === 0 ? '#fff' : '#F8FAFC', borderBottom: '1px solid #F1F5F9' }}>

                {/* Name */}
                <td style={{ padding: '10px 14px', fontWeight: 600, color: '#0F172A', whiteSpace: 'nowrap' }}>
                  {lead.name || <span style={{ color: '#CBD5E1' }}>—</span>}
                </td>

                {/* Email */}
                <td style={{ padding: '10px 14px' }}>
                  {lead.email
                    ? <a href={`mailto:${lead.email}`} style={{ color: '#1D4ED8', fontSize: 12 }}>{lead.email}</a>
                    : <span style={{ color: '#CBD5E1' }}>—</span>}
                </td>

                {/* Phone */}
                <td style={{ padding: '10px 14px', whiteSpace: 'nowrap', color: '#334155' }}>
                  {lead.phone || lead.caller_number || <span style={{ color: '#CBD5E1' }}>—</span>}
                </td>

                {/* Unit interest */}
                <td style={{ padding: '10px 14px', color: '#64748B', fontSize: 12 }}>
                  {lead.unit_interest || <span style={{ color: '#CBD5E1' }}>—</span>}
                </td>

                {/* Callback time */}
                <td style={{ padding: '10px 14px', color: '#64748B', fontSize: 12 }}>
                  {lead.callback_time || <span style={{ color: '#CBD5E1' }}>—</span>}
                </td>

                {/* Assistant name */}
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>
                  {configName(lead.voice_config_id)}
                </td>

                {/* Call duration */}
                <td style={{ padding: '10px 14px', fontSize: 12, color: '#64748B', whiteSpace: 'nowrap' }}>
                  {formatDuration(lead.call_duration_seconds)}
                </td>

                {/* Created at */}
                <td style={{ padding: '10px 14px', fontSize: 11.5, color: '#64748B', whiteSpace: 'nowrap' }}>
                  {formatDate(lead.createdAt)}
                </td>

                {/* CRM status */}
                <td style={{ padding: '10px 14px', textAlign: 'center' }}>
                  {lead.crm_pushed
                    ? (
                      <Tooltip title="Successfully pushed to CRM" arrow>
                        <span><FeatherIcon icon="check-circle" size={14} color="#059669" /></span>
                      </Tooltip>
                    ) : (
                      <Tooltip title={lead.crm_push_error || 'Not pushed to CRM'} arrow>
                        <span><FeatherIcon icon="minus-circle" size={14} color="#94A3B8" /></span>
                      </Tooltip>
                    )}
                </td>

                {/* Status badge */}
                <td style={{ padding: '10px 14px' }}>
                  <StatusBadge status={lead.status} />
                </td>

                {/* Actions */}
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'nowrap' }}>
                    {['contacted', 'scheduled', 'closed']
                      .filter((s) => s !== lead.status)
                      .map((s) => (
                        <button
                          key={s}
                          type="button"
                          disabled={updatingId === lead._id}
                          onClick={() => handleStatusChange(lead._id, s)}
                          style={{
                            padding: '3px 9px', borderRadius: 6,
                            border: '1.5px solid #E2E8F0', background: '#fff',
                            fontSize: 10.5, fontWeight: 600, color: '#64748B',
                            cursor: updatingId === lead._id ? 'not-allowed' : 'pointer',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {updatingId === lead._id ? '…' : s}
                        </button>
                      ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Box>

      {/* ── Pagination ──────────────────────────────────────────────────── */}
      {totalPages > 1 && (
        <div style={{ padding: '12px 16px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination size="sm" className="mb-0">
            <Pagination.Prev onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next onClick={() => currentPage < totalPages && setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}
    </Paper>
  );
};

export { VoiceLeadsTable };